/**
 * CollapsibleSection - A collapsible/expandable section wrapper
 * Remembers state in localStorage (sticky)
 * Consistent left-side collapse indicator
 * Supports optional headerActions for additional controls
 */

import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CollapsibleSection = ({ 
  id, 
  title, 
  icon: Icon, 
  children, 
  defaultOpen = true,
  iconColor = '#0FFFE2',
  badge = null,
  headerActions = null // Optional: React node for header controls (sound toggle, refresh, etc.)
}) => {
  const storageKey = `admin_section_${id}`;
  
  // Initialize from localStorage or default
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored !== null ? stored === 'true' : defaultOpen;
  });

  // Persist state to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, String(isOpen));
  }, [isOpen, storageKey]);

  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl overflow-hidden">
      {/* Header - Always visible */}
      <div className="p-4 flex items-center justify-between hover:bg-[#4A0A66]/10 transition-colors">
        {/* Left side: Collapse toggle + Title (clickable) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 flex-1 group"
          data-testid={`collapse-toggle-${id}`}
        >
          {/* Collapse indicator - LEFT SIDE */}
          <div className="flex items-center justify-center w-6 h-6 rounded bg-[#4A0A66]/30 group-hover:bg-[#4A0A66]/50 transition-colors">
            {isOpen ? (
              <ChevronDown className="w-4 h-4 text-[#FDF2F8]/70" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[#FDF2F8]/70" />
            )}
          </div>
          
          {/* Icon and Title */}
          {Icon && <Icon className="w-5 h-5" style={{ color: iconColor }} />}
          <h3 className="text-lg font-semibold text-white text-left">{title}</h3>
          
          {/* Badge (if any) */}
          {badge && (
            <span className="px-2 py-0.5 bg-[#df1283] text-white text-xs rounded-full">
              {badge}
            </span>
          )}
        </button>
        
        {/* Right side: Header actions (if any) + subtle hint */}
        <div className="flex items-center gap-3">
          {headerActions}
          <span className="text-xs text-[#FDF2F8]/30 hidden sm:inline">
            {isOpen ? 'collapse' : 'expand'}
          </span>
        </div>
      </div>
      
      {/* Content - Collapsible */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="p-4 pt-0 border-t border-[#4A0A66]/30">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
